import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@mui/icons-material";
import DataTable, { type Column } from "@/components/DataTable";
import { useMasterLlmModels } from "../hooks/useMasterLlmModels";
import type { Portal, PortalLlmModelInput, MasterLlmModel } from "@/types";

interface LlmModelsTabProps {
  portal: Portal;
  onSave: (models: PortalLlmModelInput[]) => void;
  isUpdating: boolean;
}

const LlmModelsTab: React.FC<LlmModelsTabProps> = ({
  portal,
  onSave,
  isUpdating,
}) => {
  const [assignedModels, setAssignedModels] = useState<PortalLlmModelInput[]>(
    (portal.llm_models ?? []).map((m) => ({
      model_id: m.model_id,
      is_default: m.is_default,
      is_enabled: m.is_enabled,
    }))
  );

  const [selectedModel, setSelectedModel] = useState<MasterLlmModel | null>(
    null
  );

  const { chatModels, isLoading: isMasterLoading } = useMasterLlmModels();

  // Lookup map: model_id -> master model details
  const masterModelMap = useMemo(() => {
    const map = new Map<string, MasterLlmModel>();
    for (const m of chatModels) {
      map.set(m.model_id, m);
    }
    return map;
  }, [chatModels]);

  const isDirty = useMemo(() => {
    const original = (portal.llm_models ?? []).map((m) => ({
      model_id: m.model_id,
      is_default: m.is_default,
      is_enabled: m.is_enabled,
    }));
    return JSON.stringify(original) !== JSON.stringify(assignedModels);
  }, [portal.llm_models, assignedModels]);

  const availableModels = useMemo(() => {
    const assignedIds = new Set(assignedModels.map((m) => m.model_id));
    return chatModels
      .filter((m) => !assignedIds.has(m.model_id))
      .sort(
        (a, b) =>
          a.provider.localeCompare(b.provider) ||
          a.model_name.localeCompare(b.model_name),
      );
  }, [chatModels, assignedModels]);

  const handleAddModel = () => {
    if (!selectedModel) return;
    const newModel: PortalLlmModelInput = {
      model_id: selectedModel.model_id,
      is_default: assignedModels.length === 0,
      is_enabled: true,
    };
    setAssignedModels((prev) => [...prev, newModel]);
    setSelectedModel(null);
  };

  const handleRemoveModel = (index: number) => {
    setAssignedModels((prev) => {
      const next = prev.filter((_, i) => i !== index);
      const hadDefault = prev[index].is_default;
      if (hadDefault && next.length > 0) {
        next[0] = { ...next[0], is_default: true };
      }
      return next;
    });
  };

  const handleToggleEnabled = (index: number) => {
    setAssignedModels((prev) =>
      prev.map((m, i) =>
        i === index ? { ...m, is_enabled: !m.is_enabled } : m
      )
    );
  };

  const handleToggleDefault = (index: number) => {
    setAssignedModels((prev) =>
      prev.map((m, i) => ({
        ...m,
        is_default: i === index ? !m.is_default : false,
      }))
    );
  };

  const columns: Column<PortalLlmModelInput>[] = [
    {
      id: "provider",
      label: "Provider",
      render: (row) => {
        const master = masterModelMap.get(row.model_id);
        return (
          <Chip
            label={master?.provider ?? "—"}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      id: "modelName",
      label: "Model Name",
      render: (row) => masterModelMap.get(row.model_id)?.model_name ?? "—",
    },
    {
      id: "modelId",
      label: "Model ID",
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {row.model_id}
        </Typography>
      ),
    },
    {
      id: "enabled",
      label: "Enabled",
      align: "center",
      render: (row, index) => (
        <Switch
          checked={row.is_enabled ?? true}
          onChange={() => handleToggleEnabled(index)}
          size="small"
        />
      ),
    },
    {
      id: "default",
      label: "Default",
      align: "center",
      render: (row, index) => (
        <IconButton
          size="small"
          onClick={() => handleToggleDefault(index)}
          color={row.is_default ? "warning" : "default"}
        >
          {row.is_default ? <StarIcon /> : <StarBorderIcon />}
        </IconButton>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (_row, index) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => handleRemoveModel(index)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Autocomplete
          options={availableModels}
          groupBy={(option) => option.provider.toUpperCase()}
          getOptionLabel={(option) =>
            `${option.model_name} (${option.model_id})`
          }
          value={selectedModel}
          onChange={(_, newValue) => setSelectedModel(newValue)}
          loading={isMasterLoading}
          sx={{ flex: 1 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select a model to add"
              placeholder="Search models..."
              size="small"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.model_id}>
              <Stack>
                <Typography variant="body2">{option.model_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.model_id}
                  {option.description ? ` — ${option.description}` : ""}
                </Typography>
              </Stack>
            </li>
          )}
        />
        <Button
          variant="outlined"
          onClick={handleAddModel}
          disabled={!selectedModel}
          startIcon={<AddIcon />}
          sx={{ whiteSpace: "nowrap" }}
        >
          Add Model
        </Button>
      </Stack>

      <DataTable
        columns={columns}
        data={assignedModels}
        keyExtractor={(row) => row.model_id}
        emptyMessage="No models assigned yet. Select a model above to get started."
        paper={false}
      />

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={() => onSave(assignedModels)}
          disabled={isUpdating || !isDirty}
        >
          {isUpdating ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Save Models"
          )}
        </Button>
      </Stack>
    </Stack>
  );
};

export default LlmModelsTab;
